<template>
  <div class="lp-page community-page">
    <Navbar />

    <!-- Hero -->
    <section class="lp-hero">
      <div class="lp-container">
        <span class="lp-eyebrow">{{ isBg ? 'ОБЩНОСТ' : 'COMMUNITY' }}</span>
        <h1 class="lp-title">{{ isBg ? 'Присъединете се към нашата общност' : 'Join Our Community' }}</h1>
        <p class="lp-lead">{{ isBg ? 'Свържете се с хиляди потребители, споделяйте опит и учете от най-добрите практики' : 'Connect with thousands of users, share experiences, and learn from best practices' }}</p>
      </div>
    </section>

    <!-- Stats -->
    <section class="lp-section-sm">
      <div class="lp-container">
        <div class="lp-grid lp-grid-4 stats-grid">
          <div class="stat"><span class="lp-ico blue"><font-awesome-icon :icon="['fas','users']" /></span><div class="stat-num">5,000+</div><div class="stat-label">{{ isBg ? 'Активни Потребители' : 'Active Users' }}</div></div>
          <div class="stat"><span class="lp-ico green"><font-awesome-icon :icon="['fas','comments']" /></span><div class="stat-num">15,000+</div><div class="stat-label">{{ isBg ? 'Дискусии' : 'Discussions' }}</div></div>
          <div class="stat"><span class="lp-ico violet"><font-awesome-icon :icon="['fas','lightbulb']" /></span><div class="stat-num">3,000+</div><div class="stat-label">{{ isBg ? 'Споделени Решения' : 'Shared Solutions' }}</div></div>
          <div class="stat"><span class="lp-ico amber"><font-awesome-icon :icon="['fas','star']" /></span><div class="stat-num">98%</div><div class="stat-label">{{ isBg ? 'Удовлетвореност' : 'Satisfaction Rate' }}</div></div>
        </div>
      </div>
    </section>

    <!-- Features -->
    <section class="lp-section">
      <div class="lp-container">
        <div class="lp-section-head">
          <h2 class="lp-h2">{{ isBg ? 'Как можете да участвате' : 'How You Can Participate' }}</h2>
          <p class="lp-lead">{{ isBg ? 'Множество начини за споделяне на знания и опит' : 'Multiple ways to share knowledge and experience' }}</p>
        </div>
        <div class="lp-grid lp-grid-3">
          <div class="lp-card feat-card" v-for="(f, i) in features" :key="i">
            <span class="lp-ico" :class="f.color"><font-awesome-icon :icon="['fas', f.icon]" /></span>
            <h3 class="lp-h3">{{ isBg ? f.title.bg : f.title.en }}</h3>
            <p class="lp-text">{{ isBg ? f.desc.bg : f.desc.en }}</p>
            <a href="#" class="feat-link">{{ isBg ? f.link.bg : f.link.en }} <font-awesome-icon :icon="['fas','arrow-right']" /></a>
          </div>
        </div>
      </div>
    </section>

    <!-- Discussions -->
    <section class="lp-section">
      <div class="lp-narrow">
        <div class="disc-head">
          <h2 class="lp-h2">{{ isBg ? 'Популярни дискусии' : 'Popular Discussions' }}</h2>
          <a href="#" class="view-all">{{ isBg ? 'Виж всички' : 'View All' }} <font-awesome-icon :icon="['fas','arrow-right']" /></a>
        </div>
        <div class="disc-list">
          <div class="disc-item reveal-item" v-for="(d, i) in discussions" :key="i">
            <span class="disc-avatar"><font-awesome-icon :icon="['fas','user']" /></span>
            <div class="disc-content">
              <h4>{{ isBg ? d.title.bg : d.title.en }}</h4>
              <div class="disc-meta">
                <span class="author">{{ d.author }}</span><span class="sep">•</span>
                <span class="cat">{{ isBg ? d.cat.bg : d.cat.en }}</span><span class="sep">•</span>
                <span>{{ isBg ? d.time.bg : d.time.en }}</span>
              </div>
            </div>
            <div class="disc-stats">
              <span><font-awesome-icon :icon="['fas','comment']" /> {{ d.comments }}</span>
              <span><font-awesome-icon :icon="['fas','eye']" /> {{ d.views }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="lp-section">
      <div class="lp-container">
        <div class="lp-cta">
          <h2>{{ isBg ? 'Станете част от общността' : 'Become Part of the Community' }}</h2>
          <p>{{ isBg ? 'Започнете да споделяте знания и учете от опита на хиляди потребители' : 'Start sharing knowledge and learning from thousands of users' }}</p>
          <div class="cta-buttons">
            <a href="/register" class="lp-btn lp-btn-primary">{{ isBg ? 'Създайте акаунт' : 'Create Account' }}</a>
            <a href="#" class="lp-btn lp-btn-secondary cta-secondary">{{ isBg ? 'Разгледайте форума' : 'Browse Forum' }}</a>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Navbar from '../components/Navbar.vue';
import Footer from '../components/Footer.vue';
import { useReveal } from '../composables/useReveal';

const { locale } = useI18n();
const isBg = computed(() => locale.value === 'bg');

const features = [
  { icon: 'comments', color: 'blue', title: { bg: 'Форум за дискусии', en: 'Discussion Forum' }, desc: { bg: 'Задавайте въпроси, споделяйте решения и помагайте на други потребители', en: 'Ask questions, share solutions, and help other users' }, link: { bg: 'Посетете форума', en: 'Visit Forum' } },
  { icon: 'calendar-days', color: 'green', title: { bg: 'Събития и уебинари', en: 'Events & Webinars' }, desc: { bg: 'Участвайте в онлайн събития и научете нови техники', en: 'Join online events and learn new techniques' }, link: { bg: 'Предстоящи събития', en: 'Upcoming Events' } },
  { icon: 'book-open', color: 'violet', title: { bg: 'База знания', en: 'Knowledge Base' }, desc: { bg: 'Достъп до хиляди статии и ръководства от общността', en: 'Access thousands of articles and guides from the community' }, link: { bg: 'Разгледай', en: 'Explore' } },
  { icon: 'trophy', color: 'amber', title: { bg: 'Споделете успехи', en: 'Share Success Stories' }, desc: { bg: 'Покажете как използвате платформата и вдъхновете други', en: 'Show how you use the platform and inspire others' }, link: { bg: 'Споделете история', en: 'Share Story' } },
  { icon: 'message', color: 'cyan', title: { bg: 'Обратна връзка', en: 'Product Feedback' }, desc: { bg: 'Предлагайте нови функции и гласувайте за идеи', en: 'Suggest new features and vote for ideas' }, link: { bg: 'Споделете идеи', en: 'Share Ideas' } },
  { icon: 'user-tie', color: 'pink', title: { bg: 'Експертна помощ', en: 'Expert Help' }, desc: { bg: 'Получете съвети от сертифицирани експерти и power users', en: 'Get advice from certified experts and power users' }, link: { bg: 'Намерете експерт', en: 'Find Expert' } },
];

const discussions = [
  { title: { bg: 'Как да автоматизирам обработката на 1000+ фактури месечно?', en: 'How to automate processing 1000+ invoices monthly?' }, author: 'Иван Петров', cat: { bg: 'Автоматизация', en: 'Automation' }, time: { bg: 'Преди 2 часа', en: '2 hours ago' }, comments: 24, views: 156 },
  { title: { bg: 'Интеграция с Микробилд - най-добри практики', en: 'Microbuild Integration - Best Practices' }, author: 'Мария Георгиева', cat: { bg: 'Интеграции', en: 'Integrations' }, time: { bg: 'Преди 5 часа', en: '5 hours ago' }, comments: 18, views: 89 },
  { title: { bg: 'Споделям workflow за обработка на доставки', en: 'Sharing workflow for delivery processing' }, author: 'Георги Димитров', cat: { bg: 'Workflow', en: 'Workflow' }, time: { bg: 'Преди 1 ден', en: '1 day ago' }, comments: 32, views: 245 },
];

onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'instant' });
});

useReveal();
</script>

<style scoped>
.stats-grid { gap: 16px; }
.stat { background: var(--card); border: 1px solid var(--line); border-radius: var(--radius-sm); padding: 30px 24px; text-align: center; box-shadow: 0 26px 60px -38px rgba(31,41,51,0.32); }
.stat .lp-ico { margin: 0 auto 16px; }
.stat-num { font-size: 32px; font-weight: 700; letter-spacing: -0.03em; color: var(--ink); }
.stat-label { font-size: 14px; color: var(--muted); margin-top: 4px; }

.feat-card { display: flex; flex-direction: column; }
.feat-card .lp-h3 { margin-bottom: 8px; }
.feat-card .lp-text { flex: 1; margin-bottom: 16px; }
.feat-link { display: inline-flex; align-items: center; gap: 8px; color: var(--violet); font-weight: 600; font-size: 14.5px; transition: gap 0.2s ease; }
.feat-card:hover .feat-link { gap: 12px; }

.disc-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 26px; gap: 16px; }
.view-all { display: inline-flex; align-items: center; gap: 8px; color: var(--violet); font-weight: 600; font-size: 14.5px; white-space: nowrap; }
.disc-list { display: flex; flex-direction: column; gap: 12px; }
.disc-item { display: flex; align-items: center; gap: 16px; background: var(--card); border: 1px solid var(--line); border-radius: var(--radius-sm); padding: 18px 22px; box-shadow: 0 16px 40px -34px rgba(31,41,51,0.3); transition: border-color 0.2s ease; }
.disc-item:hover { border-color: rgba(43,130,240,0.35); }
.disc-avatar { width: 44px; height: 44px; border-radius: 50%; background: rgba(43,130,240,0.1); color: var(--violet); display: inline-flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.disc-content { flex: 1; min-width: 0; }
.disc-content h4 { font-size: 15.5px; font-weight: 700; color: var(--ink); margin: 0 0 6px; }
.disc-meta { display: flex; flex-wrap: wrap; gap: 6px; font-size: 12.5px; color: var(--muted); align-items: center; }
.disc-meta .author { color: var(--ink); font-weight: 600; }
.disc-meta .cat { color: var(--violet); font-weight: 600; }
.disc-meta .sep { opacity: 0.5; }
.disc-stats { display: flex; gap: 16px; flex-shrink: 0; }
.disc-stats span { display: inline-flex; align-items: center; gap: 5px; font-size: 13px; color: var(--muted); font-weight: 600; }

.cta-buttons { display: inline-flex; gap: 14px; flex-wrap: wrap; justify-content: center; }
.cta-secondary { background: rgba(255,255,255,0.1); color: #fff; border-color: rgba(255,255,255,0.25); }
.cta-secondary:hover { background: rgba(255,255,255,0.18); color: #fff; border-color: rgba(255,255,255,0.4); }

@media (max-width: 600px) {
  .disc-item { flex-wrap: wrap; }
  .disc-stats { width: 100%; padding-left: 60px; }
}
</style>
